'use client';
import { useState, useEffect } from 'react';
import { MapPin, X, Navigation } from 'lucide-react';
import { useCityStore } from '@/store/city.store';
import api from '@/lib/api';

export default function CityPickerModal() {
  const { hasChosen, setCity, setHasChosen } = useCityStore();
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [detecting, setDetecting] = useState(false);
  const [detectedCity, setDetectedCity] = useState<string | null>(null);
  const [detectedLat, setDetectedLat] = useState<number | null>(null);
  const [detectedLng, setDetectedLng] = useState<number | null>(null);

  // Показываем модалку при первом визите, тихо пробуем IP
  useEffect(() => {
    if (hasChosen) return;
    setShow(true);
    // Тихий fallback по IP без попапа браузера
    fetch('https://ipapi.co/json/')
      .then(r => r.json())
      .then(async data => {
        if (data.latitude && data.longitude) {
          const res = await api.get('/cities/nearest?lat=' + data.latitude + '&lng=' + data.longitude);
          const found = res.data.data;
          if (found) {
            setDetectedCity(found.name);
            setDetectedLat(found.lat);
            setDetectedLng(found.lng);
          }
        }
      })
      .catch(() => {});
  }, [hasChosen]);

  // Поиск по вводу
  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get('/cities/search?q=' + encodeURIComponent(search))
        .then(r => setResults(r.data.data ?? []));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Кнопка "Определить моё местоположение" — GPS по клику
  const detectGPS = () => {
    if (!navigator.geolocation) return;
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await api.get('/cities/nearest?lat=' + latitude + '&lng=' + longitude);
          const found = res.data.data;
          if (found) {
            setDetectedCity(found.name);
            setDetectedLat(found.lat);
            setDetectedLng(found.lng);
          }
        } catch {}
        setDetecting(false);
      },
      () => setDetecting(false),
      { timeout: 8000 }
    );
  };

  const confirmDetected = () => {
    if (detectedCity) setCity(detectedCity, detectedLat ?? undefined, detectedLng ?? undefined);
    setShow(false);
  };

  const skip = () => { setHasChosen(true); setShow(false); };

  const selectCity = (c: any) => {
    setCity(c.name, c.lat, c.lng);
    setShow(false);
    setSearch('');
    setResults([]);
  };

  if (!show || hasChosen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-bg-surface rounded-2xl shadow-xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MapPin size={20} style={{ color: 'var(--color-brand)' }} />
            <span className="font-bold text-text-primary text-lg">Ваш город</span>
          </div>
          <button onClick={skip} className="text-gray-400 hover:text-text-secondary"><X size={20} /></button>
        </div>

        <p className="text-text-secondary text-sm mb-4">Укажите город, чтобы мы показали специалистов рядом с вами.</p>

        {/* Определённый по IP город */}
        {detectedCity && (
          <div className="mb-4 p-3 bg-bg-subtle rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin size={15} className="text-gray-400" />
              <span className="text-sm text-text-ui">Похоже, вы в <span className="font-semibold text-text-primary">{detectedCity}</span></span>
            </div>
            <button
              onClick={confirmDetected}
              className="text-sm font-medium px-3 py-1 rounded-lg text-white ml-2 flex-shrink-0"
              style={{ backgroundColor: 'var(--color-brand)' }}
            >
              Верно
            </button>
          </div>
        )}

        {/* Кнопка GPS */}
        <button
          onClick={detectGPS}
          disabled={detecting}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-border text-text-ui text-sm font-medium hover:bg-bg-subtle mb-3 disabled:opacity-50"
        >
          <Navigation size={15} />
          {detecting ? 'Определяем...' : 'Определить моё местоположение'}
        </button>

        {/* Поиск */}
        <input
          autoFocus
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Или введите город вручную..."
          className="w-full border border-border rounded-xl px-4 py-2.5 text-sm outline-none focus:border-gray-400 mb-1"
        />
        {results.length > 0 && (
          <div className="mt-1 border border-border-subtle rounded-xl overflow-hidden shadow-sm max-h-48 overflow-y-auto">
            {results.map((c: any) => (
              <button
                key={c.id}
                onClick={() => selectCity(c)}
                className="w-full text-left px-4 py-2.5 hover:bg-bg-subtle text-sm border-b border-gray-50 last:border-0"
              >
                <span className="font-medium text-text-primary">{c.name}</span>
                {c.region && <span className="text-gray-400 ml-2 text-xs">{c.region}</span>}
              </button>
            ))}
          </div>
        )}

        <button onClick={skip} className="w-full mt-3 py-2 text-xs text-gray-400 hover:text-text-secondary">
          Пропустить
        </button>
      </div>
    </div>
  );
}
